import type { CSSProperties } from 'react';
import type { Hold } from '../../types/climb';

/**
 * Cropping a climb photo to its holds, shared by every thumbnail that shows one
 * small. At 64px — the route-setting board's cards in the gym panel, the climb
 * rows in the member app — that is where the climb starts: both answer *which*
 * climb is this, and a whole wall shrunk to a square answers nothing. At a
 * third of the screen — the profile's grid of sends — it is the largest square
 * from the start holds to the top, so the whole climb reads at a glance.
 *
 * And at page size — the climb's own cover — it is the other way about: there
 * the climb is being read rather than recognised, so the crop only ever opens
 * *out*, until every circle is inside the frame (`fitCrop`).
 */

/** A rectangle in image fractions (0–1), x/y being its top-left corner. */
export interface FocusRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The crop is the holds' bounding box and nothing more — the corners of the
 * start holds set its size, so the thumbnail is zoomed as far in as the photo
 * allows. The only growth is squaring it up to the container (below), which
 * distortion makes unavoidable.
 */

/**
 * The bounding box of the start holds — green, by the same rule the climb
 * profile's overlay uses. Falls back to every hold when the setter never marked
 * a start, and to nothing when the climb has no holds at all.
 */
export function startHoldsBounds(holds: Hold[] | null | undefined): FocusRect | null {
  if (!holds || holds.length === 0) return null;

  const green = holds.filter((hold) => hold.color === 'green');
  return holdsBounds(green.length > 0 ? green : holds);
}

/**
 * The bounding box from the start holds to the top holds — green and red, by
 * the same rule as above — which is the climb itself: for a thumbnail big
 * enough to show it entire, the profile's grid, where a tile is a third of the
 * screen and the question is "what did I send", not "which of these forty is
 * it". Squared up by `focusCrop`, that is the largest square the two ends
 * form. Falls back to every hold when either end was never marked (then the
 * climb is wherever the circles are), and to nothing when there are none.
 */
export function startToTopBounds(holds: Hold[] | null | undefined): FocusRect | null {
  if (!holds || holds.length === 0) return null;

  const starts = holds.filter((hold) => hold.color === 'green');
  const tops = holds.filter((hold) => hold.color === 'red');
  const ends = starts.length > 0 && tops.length > 0 ? [...starts, ...tops] : holds;

  return holdsBounds(ends);
}

/** The bounding box of the given holds, circles included; nothing for none. */
export function holdsBounds(holds: Hold[] | null | undefined): FocusRect | null {
  if (!holds || holds.length === 0) return null;

  const left = Math.min(...holds.map((hold) => hold.x - hold.width / 2));
  const right = Math.max(...holds.map((hold) => hold.x + hold.width / 2));
  const top = Math.min(...holds.map((hold) => hold.y - hold.height / 2));
  const bottom = Math.max(...holds.map((hold) => hold.y + hold.height / 2));

  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * `rect` grown by `fraction` of its longer side on every edge, so the circles
 * on its edge sit inside the thumbnail rather than on its border. May reach
 * past the image: `focusCrop` slides the crop back inside it.
 */
export function padRect(rect: FocusRect, fraction: number): FocusRect {
  const pad = Math.max(rect.width, rect.height) * fraction;

  return {
    x: rect.x - pad,
    y: rect.y - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
}

/**
 * A crop of the image around `bounds`, sized to `containerAspect` (width /
 * height) so the thumbnail shows it without distortion, and kept inside the
 * image.
 *
 * Works in image pixels rather than fractions: a crop that is square in
 * fractions is only square on screen if the image itself is, so the natural
 * dimensions are what makes the aspect correction right. `minSizePx` caps the
 * zoom: the crop's short side never drops below that many photo pixels.
 */
export function focusCrop(
  bounds: FocusRect,
  naturalWidth: number,
  naturalHeight: number,
  containerAspect: number,
  minSizePx = 0
): FocusRect | null {
  if (naturalWidth <= 0 || naturalHeight <= 0 || containerAspect <= 0) return null;
  if (bounds.width <= 0 || bounds.height <= 0) return null;

  let widthPx = bounds.width * naturalWidth;
  let heightPx = bounds.height * naturalHeight;

  // Match the container's shape by growing the short side, never cropping in —
  // trimming instead would cut off the holds the crop exists to show.
  if (widthPx / heightPx < containerAspect) widthPx = heightPx * containerAspect;
  else heightPx = widthPx / containerAspect;

  // No closer in than `minSizePx` photo pixels on the short side: a start
  // hold a few pixels across, blown up to fill the box, is a blur rather
  // than a hold. A floor in pixels, not in fractions — a fraction would pull
  // every climb back to the same barely-cropped view.
  const floorScale = Math.max(minSizePx / Math.min(widthPx, heightPx), 1);
  widthPx *= floorScale;
  heightPx *= floorScale;

  // Shrink back, keeping the aspect, if that pushed it past the image.
  const fitScale = Math.min(naturalWidth / widthPx, naturalHeight / heightPx, 1);
  widthPx *= fitScale;
  heightPx *= fitScale;

  // Centre on the holds, then slide the crop back inside the image.
  const centreX = (bounds.x + bounds.width / 2) * naturalWidth;
  const centreY = (bounds.y + bounds.height / 2) * naturalHeight;
  const left = clamp(centreX - widthPx / 2, 0, naturalWidth - widthPx);
  const top = clamp(centreY - heightPx / 2, 0, naturalHeight - heightPx);

  return {
    x: left / naturalWidth,
    y: top / naturalHeight,
    width: widthPx / naturalWidth,
    height: heightPx / naturalHeight,
  };
}

/**
 * The crop a **cover** should show so that every hold is inside it: the holds'
 * box grown to the frame's shape, reaching past the edges of the photo where
 * the frame is a different shape from the climb rather than trimming any hold
 * off.
 *
 * The other half of the pair with `focusCrop`, and the opposite trade. A
 * thumbnail is a small window onto a photo and can only ever show a piece of
 * it, so its crop is kept inside the image and zooms in as far as the holds
 * allow. A cover is the climb at page size: it may be drawn *smaller* than its
 * frame, with the frame filling what is left around it, which is the only way a
 * portrait wall shows its start and its top at once in a frame that is not that
 * shape.
 *
 * Never closer in than the frame's own crop — the largest rectangle of the
 * frame's shape that fits inside the photo, which is what `object-fit: cover`
 * has always shown. A climb whose holds already fit is panned onto them and not
 * enlarged: zooming in would throw away the wall around them for nothing.
 */
export function fitCrop(
  bounds: FocusRect,
  naturalWidth: number,
  naturalHeight: number,
  containerAspect: number
): FocusRect | null {
  if (naturalWidth <= 0 || naturalHeight <= 0 || containerAspect <= 0) return null;
  if (bounds.width <= 0 || bounds.height <= 0) return null;

  let widthPx = bounds.width * naturalWidth;
  let heightPx = bounds.height * naturalHeight;

  // The frame's shape, grown on the short side — trimming the long one is what
  // cuts the holds off, which is the whole of what this exists to stop.
  if (widthPx / heightPx < containerAspect) widthPx = heightPx * containerAspect;
  else heightPx = widthPx / containerAspect;

  // The cover's own crop as a floor, so a climb that already fits is left at the
  // size it has always been shown at.
  const coverWidthPx = Math.min(naturalWidth, naturalHeight * containerAspect);
  if (widthPx < coverWidthPx) {
    widthPx = coverWidthPx;
    heightPx = coverWidthPx / containerAspect;
  }

  const centreX = (bounds.x + bounds.width / 2) * naturalWidth;
  const centreY = (bounds.y + bounds.height / 2) * naturalHeight;
  const left = slide(centreX - widthPx / 2, widthPx, naturalWidth);
  const top = slide(centreY - heightPx / 2, heightPx, naturalHeight);

  return {
    x: left / naturalWidth,
    y: top / naturalHeight,
    width: widthPx / naturalWidth,
    height: heightPx / naturalHeight,
  };
}

/**
 * Where one edge of a crop goes: centred on the holds and slid back inside the
 * photo, or — once the crop is larger than the photo in that axis — centred on
 * the photo itself, since then the whole of that axis is on screen and there is
 * nothing left to choose.
 */
function slide(start: number, size: number, extent: number): number {
  return size >= extent ? (extent - size) / 2 : clamp(start, 0, extent - size);
}

/** Whether the crop reaches past the photo, so the frame has room left to fill. */
export function cropOutruns(crop: FocusRect | null): boolean {
  if (!crop) return false;

  const slack = 1e-6;
  return (
    crop.x < -slack ||
    crop.y < -slack ||
    crop.x + crop.width > 1 + slack ||
    crop.y + crop.height > 1 + slack
  );
}

/**
 * Where an `<img>` goes so that `crop` alone fills its container: the photo
 * blown up until the crop is the whole of the box, then slid into place — a
 * crop starting at x% of the photo sits at `x / width` of its own travel, not
 * at a plain x%. Works the other way round too: a crop larger than the photo
 * leaves the picture smaller than the box, centred in it.
 *
 * The element must be absolutely positioned with no max-width for the
 * percentages to mean this; nothing for no crop, which leaves the plain
 * `object-fit: cover` the callers fall back to.
 */
export function cropImageStyle(crop: FocusRect | null): CSSProperties | undefined {
  if (!crop) return undefined;

  return {
    width: `${100 / crop.width}%`,
    height: `${100 / crop.height}%`,
    left: `${(-crop.x / crop.width) * 100}%`,
    top: `${(-crop.y / crop.height) * 100}%`,
  };
}

/**
 * The same crop as an SVG viewBox, in the photo's natural pixels — what makes
 * the marks drawn on a photo follow a crop of it. Nothing for no crop, which
 * leaves the overlay covering the whole picture.
 */
export function cropViewBox(
  crop: FocusRect | null,
  natural: { width: number; height: number } | null
): { x: number; y: number; width: number; height: number } | null {
  if (!crop || !natural) return null;

  return {
    x: crop.x * natural.width,
    y: crop.y * natural.height,
    width: crop.width * natural.width,
    height: crop.height * natural.height,
  };
}

/**
 * `background-size`/`background-position` that make `crop` fill the element.
 *
 * A background positioned at P% aligns the image's P% point with the
 * container's, so the offset of a crop is its distance divided by the room the
 * scaled image has to move — hence `x / (1 - width)` rather than plain `x`.
 */
export function cropBackgroundStyle(crop: FocusRect | null): CSSProperties {
  if (!crop) return { backgroundSize: 'cover', backgroundPosition: 'center' };

  const offset = (start: number, size: number) => (size >= 1 ? 50 : (start / (1 - size)) * 100);

  return {
    backgroundSize: `${100 / crop.width}% ${100 / crop.height}%`,
    backgroundPosition: `${offset(crop.x, crop.width)}% ${offset(crop.y, crop.height)}%`,
  };
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}
